import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faListCheck,
  faIndustry,
  faTriangleExclamation,
  faExplosion,
  faCubesStacked,
  faFileLines,
  faSun,
  faMountain,
  faScaleBalanced,
  faHammer,
  faArrowsLeftRight
} from "@fortawesome/free-solid-svg-icons";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";

import { RootState } from "../../state/store";
import { AreaKey } from "../../utilities/helpers/pageVocabulary.generated";
import { hasArea, hasTile } from "../../utilities/helpers/roleUtilities";
import {
  OPERATIONS_TARGET,
  PRODUCTION_CRUSHING_TARGET,
  PRODUCTION_LIST_TARGET,
  PRODUCTION_REHANDLE_TARGET,
  STOPPAGES_TARGET,
} from "../../utilities/helpers/dashboardLinks";
import { UserDto } from "../../DTOs/User/UserDto";

type TileResolver = (user: UserDto | null) => string;

type QuickTile = {
  // Note: stable identifier persisted in tblZoneTileGrant - never rename an existing key,
  // it would silently disconnect stored grants from this tile.
  key: string;
  to: string | TileResolver;
  label: string;
  icon: IconDefinition;
  areas: AreaKey[];
  accentClass: string;
};

export const TILES: QuickTile[] = [
  {
    key: "operations",
    to: OPERATIONS_TARGET.to,
    label: "Operations",
    icon: faListCheck,
    areas: [OPERATIONS_TARGET.area],
    accentClass: "text-theme-info",
  },
  {
    key: "production",
    to: PRODUCTION_LIST_TARGET.to,
    label: "Production",
    icon: faIndustry,
    areas: [PRODUCTION_LIST_TARGET.area],
    accentClass: "text-theme-danger",
  },
  {
    key: "crushing",
    to: PRODUCTION_CRUSHING_TARGET.to,
    label: "Crushing",
    icon: faHammer,
    areas: [PRODUCTION_CRUSHING_TARGET.area],
    accentClass: "text-theme-warning",
  },
  {
    key: "rehandle",
    to: PRODUCTION_REHANDLE_TARGET.to,
    label: "Rehandle",
    icon: faArrowsLeftRight,
    areas: [PRODUCTION_REHANDLE_TARGET.area],
    accentClass: "text-theme-info",
  },
  {
    key: "stoppages",
    to: STOPPAGES_TARGET.to,
    label: "Stoppages",
    icon: faTriangleExclamation,
    areas: [STOPPAGES_TARGET.area],
    accentClass: "text-theme-warning",
  },
  {
    key: "drill-blast",
    to: "/db/bh-drilling-list",
    label: "Drill & Blast",
    icon: faExplosion,
    areas: ["BlastHoleDrillingList"],
    accentClass: "text-theme-danger",
  },
  {
    key: "stockpiles",
    to: "/geology/stockpile-balances",
    label: "Stockpiles",
    icon: faCubesStacked,
    areas: ["StockpileBalances"],
    accentClass: "text-theme-success",
  },
  {
    key: "geology-mvt",
    to: "/geology/mining-movements",
    label: "Geology Mvt",
    icon: faMountain,
    areas: ["MiningMvtList"],
    accentClass: "text-theme-success",
  },
  {
    key: "ore-tally",
    to: (user) =>
      hasArea(user, "GeologyOreTally") ? "/geology/ore-tally" : "/production/ore-tally",
    label: "Ore Tally",
    icon: faScaleBalanced,
    areas: ["GeologyOreTally", "ProductionOreTally"],
    accentClass: "text-theme-info-hover",
  },
  {
    key: "daily-report",
    to: "/reports/daily",
    label: "Daily Report",
    icon: faFileLines,
    areas: ["DailyReport"],
    accentClass: "text-theme-info",
  },
  {
    key: "morning-meeting",
    to: "/reports/morning",
    label: "Morning Meeting",
    icon: faSun,
    areas: ["MorningReport"],
    accentClass: "text-theme-warning",
  },
];

export const DashboardQuickTiles: React.FC = () => {
  const { user } = useSelector((s: RootState) => s.authReducer);

  const visibleTiles = useMemo(() => {
    return TILES.filter((tile) => tile.areas.some((area) => hasArea(user, area)) && hasTile(user, tile.key));
  }, [user]);

  if (visibleTiles.length === 0) return null;

  return (
    <div className="w-full">
      <div className="grid gap-3 md:gap-4 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-9">
        {visibleTiles.map((tile) => {
          const resolvedTo =
            typeof tile.to === "function"
              ? tile.to(user)
              : tile.to;
          return (
            <Link
              key={resolvedTo}
              to={resolvedTo}
              className="group flex flex-col items-center justify-center gap-2 px-3 py-4 sm:rounded-lg transition
                        border border-theme-heading-border bg-theme-surface-2 shadow-md shadow-theme-shadow text-theme-text
                        hover:shadow-sm hover:shadow-theme-shadow hover:border-theme-focus hover:translate-y-[1px]
                        active:shadow-none active:translate-y-[2px]
"
            >
              <FontAwesomeIcon
                icon={tile.icon}
                className={`text-2xl md:text-3xl ${tile.accentClass} group-hover:scale-110 transition-transform`}
              />
              <span className="text-sm md:text-base font-medium text-center leading-tight">
                {tile.label}
              </span>
            </Link>
          );
        })}
      </div>
    </div>
  );
};
